// ---------------------------------------------------------------------------
// Reusable EmptyState Component
// ---------------------------------------------------------------------------
// Used across ALL list/data pages when there's no data to display.
// 3 variants: default (neutral gray), success (green), info (blue).
// Every empty state has a helpful CTA button.
// ---------------------------------------------------------------------------

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  FileText,
  Handshake,
  Mail,
  MessageSquare,
  Plug,
  UserPlus,
  Users,
  Workflow,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EmptyStateVariant = "default" | "success" | "info";
type EmptyStateTier = 1 | 2 | 3;

export interface EmptyStateProps {
  /** Icon component (Lucide or any SVG) */
  icon?: ReactNode;
  /** Main title */
  title: string;
  /** Description text — what to do next */
  description: string;
  /** CTA button label */
  actionLabel?: string;
  /** CTA button handler */
  onAction?: () => void;
  /** CTA href for navigation-only empty states */
  actionPath?: string;
  /** Secondary action (optional) */
  secondaryLabel?: string;
  onSecondary?: () => void;
  secondaryPath?: string;
  /** Visual variant */
  variant?: EmptyStateVariant;
  /** Custom class for container */
  className?: string;
  /** Empty-state richness tier from the onboarding vision */
  tier?: EmptyStateTier;
  /** Small overline / section label */
  eyebrow?: string;
  /** Preview panel for Tier 2/3 states */
  preview?: ReactNode;
  /** Checklist callouts for setup-oriented empty states */
  checklistItems?: string[];
  /** Additional supporting content below the main hero */
  supportingContent?: ReactNode;
  /** Optional AI prompt suggestion rendered as muted helper text */
  aiHint?: string;
}

// ---------------------------------------------------------------------------
// Variant Styles
// ---------------------------------------------------------------------------

const VARIANT_STYLES: Record<EmptyStateVariant, {
  iconBg: string;
  iconColor: string;
  buttonBg: string;
  buttonHover: string;
}> = {
  default: {
    iconBg: "bg-[var(--color-muted)]",
    iconColor: "text-[var(--color-muted-foreground)]",
    buttonBg: "bg-[var(--color-foreground)] text-[var(--color-background)]",
    buttonHover: "hover:bg-[var(--color-foreground)]/90",
  },
  success: {
    iconBg: "bg-[var(--color-success)]/5",
    iconColor: "text-[var(--color-success)]/500",
    buttonBg: "bg-[var(--color-success)] text-white",
    buttonHover: "hover:bg-[var(--color-success)]",
  },
  info: {
    iconBg: "bg-[var(--color-primary)]/5",
    iconColor: "text-[var(--color-primary)]",
    buttonBg: "bg-[var(--color-primary)] text-white",
    buttonHover: "hover:bg-[var(--color-primary)]/90",
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionPath,
  secondaryLabel,
  onSecondary,
  secondaryPath,
  variant = "default",
  className = "",
  tier = 1,
  eyebrow,
  preview,
  checklistItems,
  supportingContent,
  aiHint,
}: EmptyStateProps) {
  const styles = VARIANT_STYLES[variant];
  const hasSecondaryColumn = tier >= 2 && (preview || checklistItems?.length || supportingContent);

  const hero = (
    <div className="space-y-4">
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
          {eyebrow}
        </p>
      ) : null}

      {icon && (
        <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${styles.iconBg} ${styles.iconColor}`}>
          <div className="h-8 w-8">{icon}</div>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-[var(--color-foreground)]">{title}</h3>
        <p className="max-w-2xl text-sm text-[var(--color-muted-foreground)]">{description}</p>
        {aiHint ? (
          <p className="max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Try: &quot;{aiHint}&quot; in the AI bar
          </p>
        ) : null}
      </div>

      {(actionLabel && (onAction || actionPath)) || (secondaryLabel && (onSecondary || secondaryPath)) ? (
        <div className="flex flex-wrap items-center gap-3">
          {actionLabel && (actionPath || onAction) ? (
            actionPath ? (
              <a
                href={actionPath}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${styles.buttonBg} ${styles.buttonHover}`}
              >
                {actionLabel}
              </a>
            ) : (
              <button
                onClick={onAction}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${styles.buttonBg} ${styles.buttonHover}`}
              >
                {actionLabel}
              </button>
            )
          ) : null}
          {secondaryLabel && (secondaryPath || onSecondary) ? (
            secondaryPath ? (
              <a
                href={secondaryPath}
                className="px-4 py-2 rounded-lg text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
              >
                {secondaryLabel}
              </a>
            ) : (
              <button
                onClick={onSecondary}
                className="px-4 py-2 rounded-lg text-sm font-medium text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
              >
                {secondaryLabel}
              </button>
            )
          ) : null}
        </div>
      ) : null}

      {tier === 1 && supportingContent ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 text-left">
          {supportingContent}
        </div>
      ) : null}
    </div>
  );

  const secondaryPanel = hasSecondaryColumn ? (
    <div className="space-y-4">
      {preview ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4">
          {preview}
        </div>
      ) : null}

      {checklistItems?.length ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)]/90 p-4 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            First steps
          </p>
          <ul className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
            {checklistItems.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1 h-2 w-2 rounded-full bg-[var(--color-primary)]/70" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {tier >= 2 && supportingContent ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 text-left">
          {supportingContent}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      className={`rounded-3xl border border-[var(--color-border)] bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.10),_transparent_48%),var(--color-background)] px-6 py-8 ${className}`}
      data-empty-tier={tier}
    >
      <div className={hasSecondaryColumn ? "grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:items-start" : "max-w-3xl"}>
        {hero}
        {secondaryPanel}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-configured Empty States for Common Pages
// ---------------------------------------------------------------------------

const EMPTY_STATE_CONFIGS = {
  contacts: {
    title: "No contacts yet",
    description: "Import your first contacts or add them manually to start building relationships.",
    actionLabel: "Import Contacts",
    variant: "info" as const,
  },
  deals: {
    title: "No deals in your pipeline",
    description: "Create your first deal to start tracking revenue opportunities through your pipeline.",
    actionLabel: "Create Deal",
    variant: "info" as const,
  },
  inbox: {
    title: "All caught up!",
    description: "No new messages. When customers reach out via SMS, WhatsApp, or email, they'll appear here.",
    variant: "success" as const,
  },
  campaigns: {
    title: "No campaigns yet",
    description: "Create your first email campaign to reach your contacts with targeted messaging.",
    actionLabel: "Create Campaign",
    variant: "info" as const,
  },
  workflows: {
    title: "No automations yet",
    description: "Build your first workflow to automate follow-ups, nurture sequences, and task management.",
    actionLabel: "Build Workflow",
    variant: "info" as const,
  },
  services: {
    title: "No services listed",
    description: "Add your services so customers can discover and book appointments online.",
    actionLabel: "Add Service",
    variant: "info" as const,
  },
  analytics: {
    title: "No data yet",
    description: "Analytics will populate as your contacts interact with your pages, emails, and campaigns.",
    variant: "default" as const,
  },
  pages: {
    title: "No pages yet",
    description: "Create your first landing page with AI — describe what you want and watch it build.",
    actionLabel: "Create Page",
    variant: "info" as const,
  },
  courses: {
    title: "No courses yet",
    description: "Create your first course with modules, lessons, and quizzes to teach your audience.",
    actionLabel: "Create Course",
    variant: "info" as const,
  },
  reviews: {
    title: "No reviews yet",
    description: "Connect your review sources (Google, Facebook) to monitor and respond to customer reviews.",
    actionLabel: "Connect Reviews",
    variant: "info" as const,
  },
};

interface SmartEmptyStateConfig {
  icon: LucideIcon;
  title: string;
  description: string;
  aiHint?: string;
  primaryAction: { label: string; url: string };
  secondaryAction?: { label: string; url: string };
}

export const SMART_EMPTY_STATES: Record<string, SmartEmptyStateConfig> = {
  contacts: {
    icon: Users,
    title: "No contacts yet",
    description: "Import from a CSV, connect an integration, or add contacts manually.",
    aiHint: "Import my contacts from a CSV",
    primaryAction: { label: "Import Contacts", url: "/crm/contacts/import" },
    secondaryAction: { label: "Add Manually", url: "/crm/contacts/new" },
  },
  deals: {
    icon: Handshake,
    title: "No deals in pipeline",
    description: "Create your first deal to start tracking revenue opportunities.",
    aiHint: "Create my first deal pipeline",
    primaryAction: { label: "Create Deal", url: "/crm/deals/new" },
  },
  conversations: {
    icon: MessageSquare,
    title: "Inbox is empty",
    description: "Messages from SMS, WhatsApp, and email will appear here.",
    aiHint: "Help me configure messaging channels",
    primaryAction: { label: "Configure Channels", url: "/settings/messaging" },
  },
  workflows: {
    icon: Workflow,
    title: "No automations yet",
    description: "Create a workflow to automate follow-ups, nurture sequences, and more.",
    aiHint: "Build a follow-up workflow",
    primaryAction: { label: "Create Workflow", url: "/workflows/new" },
    secondaryAction: { label: "Browse Templates", url: "/workflows/templates" },
  },
  pages: {
    icon: FileText,
    title: "No pages published",
    description: "Build landing pages, forms, and funnels with the drag-and-drop editor.",
    aiHint: "Create a new landing page",
    primaryAction: { label: "Create Page", url: "/pages/editor/new" },
  },
  emails: {
    icon: Mail,
    title: "No email campaigns",
    description: "Create your first email campaign to reach your audience.",
    aiHint: "Draft my first email campaign",
    primaryAction: { label: "New Campaign", url: "/email/campaigns/new" },
  },
  team: {
    icon: UserPlus,
    title: "Just you for now",
    description: "Invite team members to collaborate on contacts, deals, and messaging.",
    primaryAction: { label: "Invite Team Member", url: "/settings/team/invite" },
  },
  integrations: {
    icon: Plug,
    title: "No integrations connected",
    description: "Connect your calendar, payment processor, and other tools.",
    primaryAction: { label: "Browse Integrations", url: "/settings/integrations" },
  },
  affiliate: {
    icon: Handshake,
    title: "No referrals yet",
    description: "Share your referral link to start earning commissions.",
    primaryAction: { label: "Copy Referral Link", url: "/affiliate" },
  },
};

export function SmartEmptyState({ module }: { module: keyof typeof SMART_EMPTY_STATES }) {
  const config = SMART_EMPTY_STATES[module];
  if (!config) {
    return null;
  }

  return (
    <EmptyState
      icon={<config.icon className="h-8 w-8" aria-hidden="true" />}
      title={config.title}
      description={config.description}
      aiHint={config.aiHint}
      actionLabel={config.primaryAction.label}
      actionPath={config.primaryAction.url}
      secondaryLabel={config.secondaryAction?.label}
      secondaryPath={config.secondaryAction?.url}
      className="rounded-lg border-dashed text-center"
    />
  );
}

function AiActivityIndicator({ isActive, label }: { isActive: boolean; label?: string }) {
  if (!isActive) {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-primary">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      {label ?? "AI active"}
    </span>
  );
}
